import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    const envBaseUrl = (
      import.meta.env.VITE_API_BASE_URL as string | undefined
    )?.trim();
    const resolvedBaseUrl = this.resolveBaseUrl(envBaseUrl);

    if (import.meta.env.PROD && (!envBaseUrl || envBaseUrl.length === 0)) {
      console.warn(
        '[ApiService] VITE_API_BASE_URL is not set in production. Falling back to /api.'
      );
    }

    this.api = axios.create({
      baseURL: resolvedBaseUrl,
      timeout: 10000,
    });

    this.setupInterceptors();
  }

  private resolveBaseUrl(envBaseUrl?: string): string {
    if (!envBaseUrl || envBaseUrl.length === 0) {
      return '/api';
    }

    const trimmed = envBaseUrl.trim().replace(/\/+$/, '');
    const isAbsolute = /^https?:\/\//i.test(trimmed);

    if (!isAbsolute) {
      if (trimmed === '/api' || trimmed.endsWith('/api')) {
        return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
      }
      return `${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}/api`;
    }

    try {
      const url = new URL(trimmed);
      const pathname = url.pathname.replace(/\/+$/, '');

      if (!pathname || pathname === '/') {
        url.pathname = '/api';
      } else if (!pathname.endsWith('/api')) {
        url.pathname = `${pathname}/api`;
      }

      return url.toString().replace(/\/+$/, '');
    } catch {
      return trimmed;
    }
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      config => {
        const token = localStorage.getItem('ua_designs_token');

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async error => {
        const requestUrl = String(error.config?.url || '');
        const isAuthRequest =
          requestUrl.includes('/auth/login') ||
          requestUrl.includes('/auth/register');
        const isAiRequest = requestUrl.includes('/ai/');
        const isQuietRequest =
          isAiRequest ||
          requestUrl.includes('/stakeholders/conversations/pusher/auth');
        const isPublicAuthPage =
          window.location.pathname === '/login' ||
          window.location.pathname === '/register' ||
          window.location.pathname === '/forgot-password';

        // Handle different error types
        if (error.response?.status === 401) {
          if (isAuthRequest || isPublicAuthPage) {
            return Promise.reject(error);
          }

          // Clear invalid token and redirect to login
          localStorage.removeItem('ua_designs_token');
          localStorage.removeItem('ua_designs_user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        } else if (error.response?.status === 403) {
          if (!isQuietRequest) {
            toast.error(
              error.response?.data?.message ||
                'You do not have permission to perform this action.'
            );
          }
        } else if (error.response?.status === 404) {
          if (!isQuietRequest) {
            toast.error('Resource not found');
          }
        } else if (error.response?.status === 500) {
          if (!isQuietRequest) {
            toast.error('Internal server error. Please try again later.');
          }
        } else if (error.code === 'ECONNABORTED') {
          if (!isQuietRequest) {
            toast.error('Request timeout. Please check your connection.');
          }
        } else if (!error.response) {
          if (!isQuietRequest) {
            toast.error('Network error. Please check your connection.');
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Generic HTTP methods
  async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config);
  }

  async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config);
  }

  async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(url, data, config);
  }

  async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config);
  }

  // File upload method
  async uploadFile<T>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<AxiosResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.api.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(progress);
        }
      },
    });
  }

  // Download file method
  async downloadFile(url: string, filename?: string): Promise<void> {
    const response = await this.api.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Get base URL
  getBaseURL(): string {
    return this.api.defaults.baseURL || '/api';
  }
}

export const apiService = new ApiService();
